/**
 * Phase 107 (Plan 107-08 / SLA-PRICE-01, D-04, D-06, D-07, D-08, D-09) — the
 * 24x7 price-band paint grid.
 *
 * Ported near-verbatim from the deleted `frontend/src/components/coverage/CoverageGrid.tsx`
 * (git show bec322bc): the `mousedown` -> `mouseenter` -> `mouseup` drag-paint
 * state machine, the rectangle derivation (`rectCells`), the cell footprint
 * (`h-10 w-28`), the `td` padding (`px-1 py-0.5`), the aria-label logic and
 * the hover ring on painted cells all carry over unchanged. Everything that
 * import-referenced `TeamDTO`, `ProjectCoverageCellDTO` or a per-cell rate
 * has been rebuilt from scratch against this phase's own DTOs — those types
 * no longer exist (Phase 104 retired the whole `components/coverage/`
 * directory).
 *
 * Renamed from the recovered version: `teams` -> `bands` (D-05: a band is a
 * named, coloured label carrying a rate; there is no team concept anywhere
 * in this directory), `activeTeamId` -> `armedBandId`, `projectCurrency` ->
 * `currency` (D-14: one currency per configuration, no per-cell fallback).
 *
 * This component is **injectable** (107-RESEARCH.md Pattern 1): it takes its
 * data and callbacks purely as props and fetches nothing itself. It does not
 * import `useSlaPricing` — the page owns fetching and passes data down, so a
 * later phase can mount this exact component against project-scoped data
 * with runtime effects attached without touching this file.
 *
 * D-08 (erase semantics): erase mode uses the same gestures, but the
 * emitted coordinates carry no band identity at all — `onPaintRange` always
 * emits plain `{ dayOfWeek, hour }` pairs, and the calling page decides
 * whether to assign the armed band or delete the cell from its local
 * painted set. This component has no representation of "painted but
 * band-less" — a cell is either in `cells` (painted) or it is not.
 *
 * D-15 (display-only timezone columns): `selectedZones` renders extra
 * read-only hour-label columns via `convertHourToZone`. Those columns carry
 * no mouse handlers at all, so they can never trigger `onPaintRange`.
 */
import { useState, useCallback, useMemo } from 'react';
import { convertHourToZone, CURATED_ZONES } from './tzUtils';
import type { SlaPricingCell, SlaPricingBand } from '../../services/api/slaPricing';
import { LEGACY_EN_NUMBER_LOCALE, formatNumber } from '../../utils/localeFormat';

export interface CellCoord {
  dayOfWeek: number;
  hour: number;
}

interface PricingGridProps {
  cells: SlaPricingCell[];
  bands: SlaPricingBand[];
  armedBandId: number | null;
  isErasing: boolean;
  disabled?: boolean;
  /** One currency per configuration (D-14) — no per-cell fallback. */
  currency: string;
  onPaintRange: (coords: CellCoord[]) => void;
  /** The configuration's base timezone — the pinned hour-axis column. */
  baseZone: string;
  /** Extra IANA zone ids rendered as read-only columns (D-15). */
  selectedZones?: string[];
}

export const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
const HOURS = Array.from({ length: 24 }, (_, i) => i);

function formatHour(h: number): string {
  return `${String(h).padStart(2, '0')}:00`;
}

// Phase 47 (D-04) origin, ported unchanged — Kč is the only Czech-adjacent
// literal here and it is already on the project's Czech-literal allowlist
// (a currency symbol, not a language string).
export const CURRENCY_SYMBOL: Record<string, string> = {
  EUR: '€',
  USD: '$',
  CZK: 'Kč',
};

function symbolFor(currency: string): string {
  return CURRENCY_SYMBOL[currency] ?? currency;
}

/**
 * Renders an already-exact rate string for display. This is the one place
 * in the phase's frontend that passes a rate through `Number(...)`, and it
 * is legitimate because this function performs **no arithmetic** — it only
 * reformats a value that was already computed elsewhere. All actual money
 * arithmetic lives in `priceMath.ts`.
 */
export function formatBandRate(rate: string | null, currency: string): string {
  const sym = symbolFor(currency);
  if (rate === null) return `— ${sym}`;
  const n = Number(rate);
  if (!Number.isFinite(n)) return `— ${sym}`;
  return `${formatNumber(n, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }, LEGACY_EN_NUMBER_LOCALE)} ${sym}`;
}

interface DragState {
  startDay: number;
  startHour: number;
  endDay: number;
  endHour: number;
}

function rectCells(state: DragState): CellCoord[] {
  const minDay = Math.min(state.startDay, state.endDay);
  const maxDay = Math.max(state.startDay, state.endDay);
  const minHour = Math.min(state.startHour, state.endHour);
  const maxHour = Math.max(state.startHour, state.endHour);
  const out: CellCoord[] = [];
  for (let d = minDay; d <= maxDay; d++) {
    for (let h = minHour; h <= maxHour; h++) {
      out.push({ dayOfWeek: d, hour: h });
    }
  }
  return out;
}

export function PricingGrid({
  cells,
  bands,
  armedBandId,
  isErasing,
  disabled = false,
  currency,
  onPaintRange,
  baseZone,
  selectedZones = [],
}: PricingGridProps) {
  const [drag, setDrag] = useState<DragState | null>(null);

  const bandById = useMemo(() => {
    const m = new Map<number, SlaPricingBand>();
    for (const b of bands) m.set(b.id, b);
    return m;
  }, [bands]);

  const cellByKey = useMemo(() => {
    const m = new Map<string, SlaPricingCell>();
    for (const c of cells) m.set(`${c.dayOfWeek}-${c.hour}`, c);
    return m;
  }, [cells]);

  const previewKeys = useMemo(() => {
    if (!drag) return new Set<string>();
    const set = new Set<string>();
    for (const c of rectCells(drag)) {
      set.add(`${c.dayOfWeek}-${c.hour}`);
    }
    return set;
  }, [drag]);

  const handleMouseDown = useCallback(
    (day: number, hour: number) => {
      if (disabled) return;
      // Painting is a no-op when nothing is armed and erase mode is off —
      // the same guard the recovered grid applied to a null active team.
      if (!isErasing && armedBandId === null) return;
      setDrag({ startDay: day, startHour: hour, endDay: day, endHour: hour });
    },
    [disabled, isErasing, armedBandId],
  );

  const handleMouseEnter = useCallback((day: number, hour: number) => {
    setDrag((prev) => (prev ? { ...prev, endDay: day, endHour: hour } : prev));
  }, []);

  const handleMouseUp = useCallback(() => {
    if (!drag) return;
    const range = rectCells(drag);
    setDrag(null);
    if (range.length > 0) onPaintRange(range);
  }, [drag, onPaintRange]);

  // If the user releases the button outside the grid, still commit.
  const handleGridLeave = useCallback(() => {
    if (drag) {
      const range = rectCells(drag);
      setDrag(null);
      if (range.length > 0) onPaintRange(range);
    }
  }, [drag, onPaintRange]);

  // While erasing, the preview shows the erase-in-progress ring rather than
  // any band color — erase carries no band identity (D-08).
  const previewBandId = isErasing ? null : armedBandId;
  const previewBand = previewBandId !== null ? (bandById.get(previewBandId) ?? null) : null;

  return (
    <div
      className="overflow-x-auto"
      onMouseLeave={handleGridLeave}
      data-testid="pricing-grid"
    >
      <table className="border-collapse select-none">
        <thead>
          <tr>
            <th className="w-20 px-2 py-2 text-right text-[12px] font-medium uppercase tracking-wide text-ms-text-secondary">
              {baseZone}
            </th>
            {selectedZones.map((zoneId) => {
              const zone = CURATED_ZONES.find((z) => z.id === zoneId);
              return (
                <th
                  key={zoneId}
                  className="w-28 px-2 py-2 text-right text-[12px] font-medium uppercase tracking-wide text-ms-text-secondary"
                >
                  {zone?.label ?? zoneId}
                </th>
              );
            })}
            {DAY_LABELS.map((label) => (
              <th
                key={label}
                className="w-32 px-2 py-2 text-center text-[12px] font-semibold uppercase tracking-wide text-ms-text-strong"
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {HOURS.map((hour) => (
            <tr key={hour}>
              <td className="px-2 py-1 text-right align-middle text-[12px] font-mono text-ms-text-secondary">
                {formatHour(hour)}
              </td>
              {selectedZones.map((zoneId) => {
                const { label, dayOffset } = convertHourToZone(hour, zoneId, baseZone);
                return (
                  <td
                    key={zoneId}
                    className="px-2 py-1 text-right align-middle text-[12px] font-mono text-ms-text-secondary"
                  >
                    {label}
                    {dayOffset !== 0 && (
                      <span className="text-[9px] text-ms-text-secondary">
                        {dayOffset > 0 ? '+1d' : '−1d'}
                      </span>
                    )}
                  </td>
                );
              })}
              {DAY_LABELS.map((_, day) => {
                const k = `${day}-${hour}`;
                const cell = cellByKey.get(k);
                const band = cell ? (bandById.get(cell.bandId) ?? null) : null;
                const isPreview = previewKeys.has(k);
                const renderBand = isPreview ? previewBand : band;
                const isErasePreview = isPreview && isErasing;

                const baseCls =
                  'mx-auto h-10 w-28 rounded flex flex-col items-center justify-center select-none transition-colors';
                let style: React.CSSProperties | undefined;
                let cls = baseCls;
                if (renderBand) {
                  // The single permitted inline-style use in this phase —
                  // band color is runtime data, not a design token.
                  style = { backgroundColor: renderBand.color, color: '#FFFFFF' };
                  cls += ' hover:ring-2 hover:ring-offset-1 hover:ring-ms-orange/40';
                  if (isPreview) {
                    cls += ' ring-2 ring-offset-1 ring-ms-text-strong';
                  }
                } else {
                  cls +=
                    ' bg-ms-canvas-light border border-dashed border-ms-border-light text-ms-text-secondary';
                  if (isErasePreview) {
                    cls += ' ring-2 ring-offset-1 ring-rose-400';
                  }
                }

                const ariaLabel = !band
                  ? `${DAY_LABELS[day]} ${formatHour(hour)} unpainted`
                  : band.hourlyRate !== null
                    ? `${DAY_LABELS[day]} ${formatHour(hour)} painted with ${band.name} at ${band.hourlyRate} ${currency} per hour`
                    : `${DAY_LABELS[day]} ${formatHour(hour)} painted with ${band.name}, no rate set`;

                return (
                  <td key={day} className="px-1 py-0.5">
                    <div
                      role="button"
                      aria-label={ariaLabel}
                      data-day={day}
                      data-hour={hour}
                      onMouseDown={() => handleMouseDown(day, hour)}
                      onMouseEnter={() => handleMouseEnter(day, hour)}
                      onMouseUp={handleMouseUp}
                      style={style}
                      className={cls}
                    >
                      {renderBand ? (
                        <div className="flex flex-col items-center justify-center gap-0.5 leading-none">
                          <span className="text-[11px] font-bold text-white leading-none">
                            {renderBand.abbreviation}
                          </span>
                          <span className="text-[11px] font-mono font-semibold text-white leading-none">
                            {formatBandRate(renderBand.hourlyRate, currency)}
                          </span>
                        </div>
                      ) : null}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default PricingGrid;
